<template>
  <v-container fluid>
    <TitleBar title="New Employee" />

    <v-card outlined class="rounded-xl glass-card mt-5">
      <CardToolbar title="Employee" :isModal="false" />

      <!-- Basic Info -->
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="text"
              v-model="employee.firstName"
              @focus="$event.target.select()"
              :error-messages="firstNameErrors"
              label="First Name"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              solo
              type="text"
              v-model="employee.lastName"
              @focus="$event.target.select()"
              label="Last Name"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              solo
              v-model="employee.pinCode"
              :append-icon="showPassword ? 'visibility_off' : 'visibility'"
              :type="showPassword ? 'text' : 'password'"
              label="Pin Code"
              counter
              :error-messages="pinErrors"
              @focus="$event.target.select()"
              @click:append="showPassword = !showPassword"
              @keyup.enter="submit"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              solo
              type="tel"
              v-model="employee.phoneNumber"
              :error-messages="phoneNumberErrors"
              label="Phone Number"
              @focus="$event.target.select()"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              solo
              type="email"
              v-model="employee.email"
              :error-messages="emailErrors"
              label="Email Address"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea solo label="Notes" auto-grow v-model="employee.note" />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Expansion Panels -->
      <v-card-text>
        <v-expansion-panels multiple class="rounded-xl">
         
          <!-- Address Panel -->
          <v-expansion-panel>
            <v-expansion-panel-header> Address </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-card outlined class="rounded-xl">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        solo
                        label="Type"
                        v-model="employee.Address.type"
                        @focus="$event.target.select()"
                        hint="Home, Office, Business"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        solo
                        label="Phone Number"
                        v-model="employee.Address.phoneNumber"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        solo
                        label="Address"
                        v-model="employee.Address.addressLine1"
                        :error-messages="addressLine1Errors"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        solo
                        label="Address Line 2"
                        v-model="employee.Address.addressLine2"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        solo
                        label="City"
                        v-model="employee.Address.city"
                        :error-messages="cityErrors"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        solo
                        label="State"
                        :items="states"
                        item-text="name"
                        item-value="id"
                        v-model="employee.Address.StateId"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        solo
                        label="Zip Code"
                        v-model="employee.Address.postalCode"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        solo
                        label="Country"
                        :items="countries"
                        item-text="name"
                        item-value="code"
                        v-model="employee.Address.CountryCode"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        solo
                        label="Description"
                        auto-grow
                        v-model="employee.Address.description"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Locations Panel -->
          <v-expansion-panel>
            <v-expansion-panel-header> Locations </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-card outlined class="rounded-xl">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        solo
                        label="Select Location"
                        :items="locations"
                        item-text="name"
                        v-model="selectedLocation"
                        return-object
                      >
                        <template #item="{ item }">
                          <span>{{ item.name }}</span>
                          <v-spacer />
                          <span v-if="item.Address">
                            {{ item.Address.addressLine1 }}
                          </span>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        solo
                        label="Select Role"
                        :items="roles"
                        item-text="name"
                        v-model="selectedRole"
                        return-object
                      />
                    </v-col>

                    <v-col cols="12" sm="2">
                      <v-btn
                        color="primary" block
                        :disabled="!selectedLocation || !selectedRole"
                        @click="addLocation"
                      >
                        Add Location
                      </v-btn>
                    </v-col>

                    <v-col cols="12">
                      <v-data-table
                        :headers="locationTable.headers"
                        :items="employee.Locations"
                        hide-default-footer
                      >
                        <template #item="{ item, index }">
                          <tr>
                            <td>{{ item.name }}</td>
                            <td>
                              {{
                                item.EmployeeRole
                                  ? item.EmployeeRole.name
                                  : "No Role"
                              }}
                            </td>
                            <td class="text-center">
                              <v-icon @click="deleteLocation(index)"
                                >delete</v-icon
                              >
                            </td>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <!-- Footer Buttons -->
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn color="primary" @click="submit">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import employeeService from "../service";
import locationService from "../../Location/service.js";
import rolesService from "../modules/Roles/service.js";

const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  requiredIf,
} = require("vuelidate/lib/validators");

export default {
  name: "employee-create",
  props: {
    isModal: Boolean,
  },
  async created() {
    try {
      this.roles = (await rolesService.getAll()).data;
      this.locations = (await locationService.getAll({ limit: 40 })).data;
    } catch (error) {
      console.log(error);
    }
  },
  data() {
    return {
      showPassword: false,
      locations: [],
      roles: [],
      selectedLocation: null,
      selectedRole: null,
      locationTable: {
        headers: [
          {
            text: "Name",
            value: "name",
            sortable: false,
          },
          {
            text: "Role",
            value: "EmployeeRole",
            sortable: false,
          },
          {
            text: "Action",
            value: "action",
            sortable: false,
            align: "center",
          },
        ],
      },
      employee: {
        firstName: null,
        lastName: null,
        pinCode: null,
        phoneNumber: null,
        email: null,
        website: null,
        note: null,
        Address: {},
        hourlyRate: null,
        Locations: [],
        metadata: {
          employeeHours: {
            breakHours: null,
            workingHours: null,
          },
          timeClockLogin: {
            admin: false,
            employee: false,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters("global", [
      "states",
      "countries",
      "defaultCountryCode",
      "location",
    ]),
    // ...mapGetters('integration', ['locationModules', 'modules']),
    Address() {
      return this.employee.Address;
    },
    pinErrors() {
      const errors = [];
      if (!this.$v.employee.pinCode.$dirty) return errors;
      if (!this.$v.employee.pinCode.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.employee.pinCode.minLength)
        errors.push(this.$t("validations.fieldMustbeatLeast4ChrLong"));
      return errors;
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.employee.firstName.$dirty) return errors;
      if (!this.$v.employee.firstName.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.employee.firstName.minLength)
        errors.push(this.$t("validations.fieldMustbeatLeast2ChrLong"));
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.employee.lastName.$dirty) return errors;
      if (!this.$v.employee.lastName.minLength)
        errors.push(this.$t("validations.fieldMustbeatLeast2ChrLong"));
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.employee.phoneNumber.$dirty) return errors;
      // if (!this.$v.employee.phoneNumber.required) errors.push(this.$t('validations.fieldIsRequired'))
      if (!this.$v.employee.phoneNumber.minLength)
        errors.push(this.$t("validations.validLetters10"));
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.employee.email.$dirty) return errors;
      if (!this.$v.employee.email.email)
        errors.push(this.$t("validations.emailIsNotValid"));
      return errors;
    },
    addressLine1Errors() {
      const errors = [];
      if (!this.$v.employee.Address.addressLine1.$dirty) return errors;
      if (!this.$v.employee.Address.addressLine1.minLength)
        errors.push(this.$t("validations.addressMustHaveAtleast2Characters"));
      if (!this.$v.employee.Address.addressLine1.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.employee.Address.city.$dirty) return errors;
      if (!this.$v.employee.Address.city.required)
        errors.push(this.$t("validations.cityIsRequired"));
      return errors;
    },
  },
  watch: {
    Address() {
      if (Object.keys(this.employee.Address).length > 0) {
        if (!this.employee.Address.city)
          this.employee.Address.city = this.location.Address.city;
        if (!this.employee.Address.StateId)
          this.employee.Address.StateId = this.location.Address.StateId;
        if (!this.employee.Address.CountryCode)
          this.employee.Address.CountryCode = this.location.Address.CountryCode;
      }
    },
  },
  methods: {
    addLocation() {
      this.selectedLocation.EmployeeRole = this.selectedRole;
      this.employee.Locations = this.employee.Locations.concat(
        this.selectedLocation
      );
      // find the selected location and remove it from the list
      const foundIndex = this.locations.findIndex(
        (element) => this.selectedLocation.id === element.id
      );
      this.locations.splice(foundIndex, 1);
      this.selectedLocation = 0;
      this.selectedRole = 0;
    },
    deleteLocation(index) {
      this.locations.push(this.employee.Locations[index]);
      this.employee.Locations.splice(index, 1);
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            timer: 2000,
          })
          .fire({
            type: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.$Progress.start();
        console.log("sending employee", this.employee);
        employeeService
          .create(this.employee)
          .then(() => {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                timer: 2000,
              })
              .fire({
                type: "success",
                title: this.$t("toastMessage.text.employeeCreated"),
              });
            this.$Progress.finish();
            this.close();
          })
          .catch((err) => {
            console.log(err);
            this.$swal
              .mixin({
                toast: true,
                showConfirmButton: true,
                position: "top-end",
                timer: 3000,
              })
              .fire({
                type: "error",
                title: err.data.message,
              });
            this.$Progress.fail();
          });
      }
    },
    cancel() {
      this.close();
    },
    close() {
      if (!this.isModal) {
        this.$router.go(-1);
      } else {
        this.$emit("close");
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    employee: {
      pinCode: {
        required,
        minLength: minLength(4),
      },
      firstName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        minLength: minLength(1),
      },
      phoneNumber: {
        minLength: minLength(10),
      },
      email: {
        email,
      },
      Address: {
        addressLine1: {
          required: requiredIf(
            (nestedModel) => Object.keys(nestedModel).length > 0
          ),
          minLength: minLength(2),
        },
        city: {
          required: requiredIf(
            (nestedModel) => Object.keys(nestedModel).length > 0
          ),
        },
      },
    },
  },
};
</script>
